import { ChatModel, ChatThread, Project } from "app-types/chat";
import { AllowedMCPServer, MCPServerInfo } from "app-types/mcp";
import { WorkflowSummary } from "app-types/workflow";
import { Agent, AgentTemplate } from "app-types/agent";
import { OPENAI_VOICE } from "lib/ai/speech/open-ai/use-voice-chat.openai";
import { AppDefaultToolkit } from "lib/ai/tools";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type Mutate<T> = (
  partial: T | Partial<T> | ((state: T) => T | Partial<T>),
  replace?: boolean,
) => void;
// Sequential processing state interfaces
export interface SequentialStep {
  id: string;
  stepNumber: number;
  description: string;
  status: "pending" | "running" | "completed" | "failed";
  output?: string;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
}

export interface SequentialProcessingState {
  isActive: boolean;
  threadId?: string;
  agentId?: string;
  steps: SequentialStep[];
  currentStepIndex: number;
  totalSteps: number;
  isCompleted: boolean;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
}

// Agent state interfaces
export interface AgentState {
  // Agent management
  availableAgents: Agent[];
  availableTemplates: AgentTemplate[];
  selectedAgent: Agent | null;
  isLoadingAgents: boolean;
  agentError: string | null;

  // Agent chat contexts
  agentThreads: Record<string, ChatThread["id"]>; // agentId -> threadId mapping

  // Sequential processing
  sequentialProcessing: SequentialProcessingState;
}

export interface AppState {
  threadList: ChatThread[];
  mcpList: (MCPServerInfo & { id: string })[];
  workflowToolList: WorkflowSummary[];
  projectList: Omit<Project, "instructions">[];
  currentThreadId: ChatThread["id"] | null;
  currentProjectId: Project["id"] | null;
  toolChoice: "auto" | "none" | "manual";
  allowedMcpServers?: Record<string, AllowedMCPServer>;
  allowedAppDefaultToolkit?: AppDefaultToolkit[];
  toolPresets: {
    allowedMcpServers?: Record<string, AllowedMCPServer>;
    allowedAppDefaultToolkit?: AppDefaultToolkit[];
    name: string;
  }[];
  chatModel?: ChatModel;
  openShortcutsPopup: boolean;
  openChatPreferences: boolean;
  mcpCustomizationPopup?: MCPServerInfo & { id: string };
  welcomeMessage: {
    isOpen: boolean;
    messageId?: string;
    content?: string;
  };
  onboardingTour: {
    isActive: boolean;
    isFirstTime: boolean;
    currentStep: number;
    isLoading: boolean;
    error: string | null;
  };
  temporaryChat: {
    isOpen: boolean;
    instructions: string;
    chatModel?: ChatModel;
  };
  voiceChat: {
    isOpen: boolean;
    threadId?: string;
    projectId?: string;
    options: {
      provider: string;
      providerOptions?: Record<string, any>;
    };
  };

  // Agent state
  agents: AgentState;
}

export interface AppDispatch {
  mutate: Mutate<AppState>;

  // Agent management actions
  setAvailableAgents: (agents: Agent[]) => void;
  setAvailableTemplates: (templates: AgentTemplate[]) => void;
  selectAgent: (agent: Agent | null) => void;
  setAgentLoading: (loading: boolean) => void;
  setAgentError: (error: string | null) => void;

  // Agent thread management
  setAgentThread: (agentId: string, threadId: string) => void;
  removeAgentThread: (agentId: string) => void;

  // Sequential processing actions
  startSequentialProcessing: (
    agentId: string,
    threadId: string,
    steps: SequentialStep[],
  ) => void;
  updateSequentialStep: (
    stepId: string,
    updates: Partial<SequentialStep>,
  ) => void;
  setCurrentSequentialStep: (stepIndex: number) => void;
  completeSequentialProcessing: () => void;
  failSequentialProcessing: (error: string) => void;
  resetSequentialProcessing: () => void;
}

const initialAgentState: AgentState = {
  availableAgents: [],
  availableTemplates: [],
  selectedAgent: null,
  isLoadingAgents: false,
  agentError: null,
  agentThreads: {},
  sequentialProcessing: {
    isActive: false,
    steps: [],
    currentStepIndex: 0,
    totalSteps: 0,
    isCompleted: false,
  },
};

const initialState: AppState = {
  threadList: [],
  projectList: [],
  mcpList: [],
  workflowToolList: [],
  currentThreadId: null,
  currentProjectId: null,
  toolChoice: "auto",
  allowedMcpServers: undefined,
  allowedAppDefaultToolkit: [],
  toolPresets: [],
  openShortcutsPopup: false,
  openChatPreferences: false,
  mcpCustomizationPopup: undefined,
  welcomeMessage: {
    isOpen: false,
  },
  onboardingTour: {
    isActive: false,
    isFirstTime: false,
    currentStep: 0,
    isLoading: true,
    error: null,
  },
  temporaryChat: {
    isOpen: false,
    instructions: "",
  },
  voiceChat: {
    isOpen: false,
    options: {
      provider: "openai",
      providerOptions: {
        model: OPENAI_VOICE["Alloy"],
      },
    },
  },
  agents: initialAgentState,
};

export const appStore = create<AppState & AppDispatch>()(
  persist(
    (set, _get) => ({
      ...initialState,
      mutate: set,

      // Agent management actions
      setAvailableAgents: (agents: Agent[]) => {
        set((state) => ({
          agents: {
            ...state.agents,
            availableAgents: agents,
            isLoadingAgents: false,
            agentError: null,
          },
        }));
      },

      setAvailableTemplates: (templates: AgentTemplate[]) => {
        set((state) => ({
          agents: {
            ...state.agents,
            availableTemplates: templates,
          },
        }));
      },

      selectAgent: (agent: Agent | null) => {
        set((state) => ({
          agents: {
            ...state.agents,
            selectedAgent: agent,
          },
        }));
      },

      setAgentLoading: (loading: boolean) => {
        set((state) => ({
          agents: {
            ...state.agents,
            isLoadingAgents: loading,
          },
        }));
      },

      setAgentError: (error: string | null) => {
        set((state) => ({
          agents: {
            ...state.agents,
            agentError: error,
            isLoadingAgents: false,
          },
        }));
      },

      // Agent thread management
      setAgentThread: (agentId: string, threadId: string) => {
        set((state) => ({
          agents: {
            ...state.agents,
            agentThreads: {
              ...state.agents.agentThreads,
              [agentId]: threadId,
            },
          },
        }));
      },

      removeAgentThread: (agentId: string) => {
        set((state) => {
          const { [agentId]: removed, ...remainingThreads } =
            state.agents.agentThreads;
          return {
            agents: {
              ...state.agents,
              agentThreads: remainingThreads,
            },
          };
        });
      },

      // Sequential processing actions
      startSequentialProcessing: (
        agentId: string,
        threadId: string,
        steps: SequentialStep[],
      ) => {
        set((state) => ({
          agents: {
            ...state.agents,
            sequentialProcessing: {
              isActive: true,
              threadId,
              agentId,
              steps,
              currentStepIndex: 0,
              totalSteps: steps.length,
              isCompleted: false,
              error: undefined,
              startedAt: new Date(),
              completedAt: undefined,
            },
          },
        }));
      },

      updateSequentialStep: (
        stepId: string,
        updates: Partial<SequentialStep>,
      ) => {
        set((state) => ({
          agents: {
            ...state.agents,
            sequentialProcessing: {
              ...state.agents.sequentialProcessing,
              steps: state.agents.sequentialProcessing.steps.map((step) =>
                step.id === stepId ? { ...step, ...updates } : step,
              ),
            },
          },
        }));
      },

      setCurrentSequentialStep: (stepIndex: number) => {
        set((state) => ({
          agents: {
            ...state.agents,
            sequentialProcessing: {
              ...state.agents.sequentialProcessing,
              currentStepIndex: stepIndex,
            },
          },
        }));
      },

      completeSequentialProcessing: () => {
        set((state) => ({
          agents: {
            ...state.agents,
            sequentialProcessing: {
              ...state.agents.sequentialProcessing,
              isCompleted: true,
              isActive: false,
              completedAt: new Date(),
            },
          },
        }));
      },

      failSequentialProcessing: (error: string) => {
        set((state) => ({
          agents: {
            ...state.agents,
            sequentialProcessing: {
              ...state.agents.sequentialProcessing,
              isActive: false,
              error,
              completedAt: new Date(),
            },
          },
        }));
      },

      resetSequentialProcessing: () => {
        set((state) => ({
          agents: {
            ...state.agents,
            sequentialProcessing: initialAgentState.sequentialProcessing,
          },
        }));
      },
    }),
    {
      name: "mc-app-store-v2.0.0",
      partialize: (state) => ({
        chatModel: state.chatModel || initialState.chatModel,
        toolChoice: state.toolChoice || initialState.toolChoice,
        allowedMcpServers:
          state.allowedMcpServers || initialState.allowedMcpServers,
        allowedAppDefaultToolkit:
          state.allowedAppDefaultToolkit ||
          initialState.allowedAppDefaultToolkit,
        temporaryChat: {
          ...initialState.temporaryChat,
          ...state.temporaryChat,
          isOpen: false,
        },
        toolPresets: state.toolPresets || initialState.toolPresets,
        welcomeMessage: {
          ...initialState.welcomeMessage,
          isOpen: false,
        },
        onboardingTour: {
          ...initialState.onboardingTour,
          isActive: false,
        },
        voiceChat: {
          ...initialState.voiceChat,
          ...state.voiceChat,
          threadId: undefined,
          projectId: undefined,
          isOpen: false,
        },
        // Persist agent state (excluding sensitive data)
        agents: {
          ...initialState.agents,
          selectedAgent: state.agents.selectedAgent,
          agentThreads: state.agents.agentThreads,
          // Don't persist sequential processing state as it's runtime-only
          sequentialProcessing: initialState.agents.sequentialProcessing,
        },
      }),
    },
  ),
);
